import StaticPageLayout, { ContentBlock } from 'components/StaticPageLayout'
import Tag from 'components/Tag'
import ComponentFAQ from 'components/directus/FAQ'
import { gridBaseStyle, otherFeaturesBaseStyle } from 'components/features-page/DesktopFeaturesPage'
import EmbeddedVideo from 'components/features-page/EmbeddedVideo'
import FeatureBox from 'components/features-page/FeatureBox'
import FeatureImage from 'components/features-page/FeatureImage'
import FeaturePageDownloadButtons from 'components/features-page/FeaturePageDownloadButtons'
import FeaturePageHero from 'components/features-page/FeaturePageHero'
import FeatureSocialProof from 'components/features-page/FeatureSocialProof'
import FeatureText from 'components/features-page/FeatureText'
import MobileHowToSetUp from 'components/features-page/MobileHowToSetUp'
import { graphql, useStaticQuery } from 'gatsby'
import { StaticImage } from 'gatsby-plugin-image'
import commandLineIcon from 'images/command-line-icon.svg'
import connectivity from 'images/connectivity.svg'
import dataEncryptedIcon from 'images/data-encrypted-icon.svg'
import globeIcon from 'images/globe-icon.svg'
import networkShield from 'images/network-shield.svg'
import noLoggingIcon from 'images/no-logging-icon.svg'
import robertIcon from 'images/robert-icon.svg'
import splitTunneling from 'images/split-tunneling-icon.svg'
import { ReactElement } from 'react'
import { Flex, Grid, Text, ThemeUIStyleObject } from 'theme-ui'
import getEnglishStrings from 'utils/getEnglishStrings'

const imageBaseStyle: ThemeUIStyleObject = {
  width: ['28rem'],
  height: '100%',
}

export default function MobileFeaturePage({ path }: { path: string }): ReactElement {
  const data = useStaticQuery<Queries.MobileFeaturePageQuery>(graphql`
    query MobileFeaturePage {
      directus {
        mobile_page {
          strings {
            android_title
            android_subtitle
            ios_title
            ios_subtitle
            available_for_android_only
            blocks_ads_and_trackers
            blocks_ads_and_trackers_subtitle
            browse_faster
            browse_faster_subtitle
            connect_on_any_network
            connect_or_disconnect
            connect_or_disconnect_second_subtitle
            connect_or_disconnect_subtitle
            custom_access_rules
            custom_access_rules_subtitle
            download_for_android
            download_for_ios
            encrypts_your_data
            ikev2_subtitle
            import_and_connect
            network_rules
            one_of_a_kind
            one_of_a_kind_subtitle
            openvpn_tcp_subtitle
            openvpn_udp_subtitle
            other_features
            prevent_infections
            prevent_infections_subtitle
            robert
            servers_in_over
            split_tunneling
            strict_no_log
            subtitle
            title
            tunnel_what_you_want
            tunnel_what_you_want_subtitle
            unparalleled_connectivity
            windscribe_offers
            wireguard_subtitle
            meta_description
            meta_keywords
            android_video_title
            ios_video_title
            android_video_url
            ios_video_url
            tldr
            stealth
            stealth_content
            wstunnel
            wstunnel_content
            languages_code {
              code
            }
          }
          ios_faq {
            items {
              faq_items_id {
                strings {
                  languages_code {
                    code
                  }
                  question
                  answer
                }
              }
            }
          }
          android_faq {
            items {
              faq_items_id {
                strings {
                  languages_code {
                    code
                  }
                  question
                  answer
                }
              }
            }
          }
          social_proof {
            id
            categories(sort: "sort") {
              social_proof_categories_id {
                id
                strings {
                  languages_code {
                    code
                  }
                  name
                }
                items(sort: "sort") {
                  social_proof_items_id {
                    id
                    url
                    score
                    strings {
                      author_description
                      author_name
                      content
                      languages_code {
                        code
                      }
                    }
                    award_badge {
                      id
                      imageFile {
                        childImageSharp {
                          gatsbyImageData(height: 30, width: 88)
                        }
                      }
                    }
                    author_picture {
                      id
                      imageFile {
                        childImageSharp {
                          gatsbyImageData(height: 50, width: 50)
                        }
                      }
                    }
                    organization_picture {
                      id
                      imageFile {
                        childImageSharp {
                          gatsbyImageData(width: 50, height: 50)
                        }
                      }
                    }
                  }
                }
              }
            }
            strings {
              title
              languages_code {
                code
              }
            }
          }
        }
        general_translations {
          strings {
            features
            languages_code {
              code
            }
            ikev2
            wireguard
            openvpn
            tcp
            udp
            watch_a_video
          }
        }
        ios_app_page {
          strings {
            languages_code {
              code
            }
            meta_description
            meta_keywords
            title
          }
        }
        android_app_page {
          strings {
            languages_code {
              code
            }
            meta_description
            meta_keywords
            title
          }
        }
      }
    }
  `)

  const mobileFeaturesData = getEnglishStrings(data.directus.mobile_page?.strings)
  const generalTranslations = getEnglishStrings(data.directus.general_translations?.strings)

  const metaData =
    path === '/features/android/'
      ? getEnglishStrings(data?.directus?.android_app_page?.strings)
      : getEnglishStrings(data?.directus?.ios_app_page?.strings)

  return (
    <StaticPageLayout
      title={`${metaData?.title} | Windscribe`}
      metaDescription={metaData?.meta_description}
      metaKeywords={metaData?.meta_keywords}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: ['hidden', 'hidden', 'visible'],
          width: '100vw',
          mb: ['4.8rem', '8rem'],
        }}
      >
        <FeaturePageHero path={path} />
        <ContentBlock
          sxx={{
            gap: ['4.8rem', '4.8rem', '8rem'],
            alignItems: 'center',
            maxWidth: '98rem',
            px: ['2.4rem', '2.4rem', 0],
          }}
        >
          <MobileHowToSetUp path={path} sxx={{ mt: '2.4rem' }} />
          <EmbeddedVideo
            videoSrc={
              path === '/features/android/'
                ? 'https://www.youtube.com/embed/vxwqdP_H2B4'
                : 'https://www.youtube.com/embed/8es-h3buZgk'
            }
          />
          <Tag title={generalTranslations?.features ?? ''} />
          <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
            <FeatureImage
              imageSide="left"
              title={mobileFeaturesData?.connect_on_any_network ?? ''}
              category={mobileFeaturesData?.unparalleled_connectivity ?? ''}
              firstSubtitle={mobileFeaturesData?.windscribe_offers ?? ''}
              icon={connectivity}
              categoryColour="windscribeBlue"
              imageStyles={{
                minWidth: '28rem',
              }}
              sxx={{
                background:
                  theme => `linear-gradient(${theme.colors?.background}, ${theme.colors?.background}) padding-box,
                            linear-gradient(to bottom, ${theme.colors?.windscribeBlue25}, ${theme.colors?.transparent}) border-box`,
                border: '0.2rem solid transparent',
                borderRadius: '4.8rem 4.8rem 0 0',
              }}
            >
              <StaticImage src="../../images/connection-app.png" alt="" sx={imageBaseStyle} />
            </FeatureImage>
            <Grid
              columns={[1, 2, 2]}
              sx={{
                ...gridBaseStyle,
                px: 0,
                mt: ['3.2rem', '3.2rem', 0],
              }}
            >
              <FeatureBox
                title={generalTranslations?.wireguard ?? ''}
                subtitle={mobileFeaturesData?.wireguard_subtitle ?? ''}
                colour="windscribeBlue"
              />
              <FeatureBox
                title={generalTranslations?.ikev2 ?? ''}
                subtitle={mobileFeaturesData?.ikev2_subtitle ?? ''}
                colour="windscribeBlue"
              />
              <FeatureBox
                title={`${generalTranslations?.openvpn} (${generalTranslations?.tcp})`}
                subtitle={mobileFeaturesData?.openvpn_tcp_subtitle ?? ''}
                colour="windscribeBlue"
              />
              <FeatureBox
                title={`${generalTranslations?.openvpn} (${generalTranslations?.udp})`}
                subtitle={mobileFeaturesData?.openvpn_udp_subtitle ?? ''}
                colour="windscribeBlue"
              />
              <FeatureBox
                title={`${mobileFeaturesData?.stealth ?? ''}`}
                subtitle={mobileFeaturesData?.stealth_content ?? ''}
                colour="windscribeBlue"
              />
              <FeatureBox
                title={`${mobileFeaturesData?.wstunnel ?? ''}`}
                subtitle={mobileFeaturesData?.wstunnel_content ?? ''}
                colour="windscribeBlue"
              />
            </Grid>
          </Flex>
          <FeatureImage
            imageSide="right"
            title={mobileFeaturesData?.connect_or_disconnect ?? ''}
            category={mobileFeaturesData?.network_rules ?? ''}
            firstSubtitle={mobileFeaturesData?.connect_or_disconnect_subtitle ?? ''}
            secondSubtitle={mobileFeaturesData?.connect_or_disconnect_second_subtitle ?? ''}
            icon={networkShield}
            categoryColour="neonGreen"
            imageStyles={{
              minWidth: '28rem',
            }}
            videoButton={{
              href: 'https://www.youtube.com/watch?v=H1BvU7QtrNQk',
              text: generalTranslations?.watch_a_video ?? '',
            }}
            sxx={{
              p: 0,
            }}
          >
            <StaticImage src="../../images/network-options-app.png" alt="" sx={imageBaseStyle} />
          </FeatureImage>
          {path === '/features/android/' && (
            <FeatureImage
              imageSide="left"
              title={mobileFeaturesData?.tunnel_what_you_want ?? ''}
              category={mobileFeaturesData?.split_tunneling ?? ''}
              firstSubtitle={mobileFeaturesData?.tunnel_what_you_want_subtitle ?? ''}
              icon={splitTunneling}
              categoryColour="gold"
              imageStyles={{ minWidth: '28rem' }}
              sxx={{
                background:
                  theme => `linear-gradient(${theme.colors?.background}, ${theme.colors?.background}) padding-box,
                            linear-gradient(to bottom, ${theme.colors?.gold25}, ${theme.colors?.transparent}) border-box`,
                border: '0.2rem solid transparent',
                borderRadius: '4.8rem 4.8rem 0 0',
              }}
              videoButton={
                path === '/features/android/'
                  ? {
                      href: 'https://www.youtube.com/watch?v=uJjkixMaFDw',
                      text: generalTranslations?.watch_a_video ?? '',
                    }
                  : undefined
              }
            >
              <StaticImage src="../../images/split-tunneling-app.png" alt="" sx={imageBaseStyle} />
            </FeatureImage>
          )}
          <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
            <FeatureImage
              imageSide={path === '/features/android/' ? 'right' : 'left'}
              title={mobileFeaturesData?.one_of_a_kind ?? ''}
              category={mobileFeaturesData?.robert ?? ''}
              firstSubtitle={mobileFeaturesData?.one_of_a_kind_subtitle ?? ''}
              icon={robertIcon}
              categoryColour="periwinkle"
              imageStyles={{ minWidth: '28rem' }}
              sxx={{
                p: 0,
              }}
            >
              <StaticImage
                src="../../images/robert-app.png"
                alt=""
                sx={{
                  width: ['28rem'],
                  height: '100%',
                  mb: '1.6rem',
                }}
              />
            </FeatureImage>
            <Grid
              columns={[1, 2, 2]}
              sx={{
                ...gridBaseStyle,
                px: 0,
                mt: ['3.2rem', '4.8rem', 0],
              }}
            >
              <FeatureBox
                title={mobileFeaturesData?.blocks_ads_and_trackers ?? ''}
                subtitle={mobileFeaturesData?.blocks_ads_and_trackers_subtitle ?? ''}
                colour="periwinkle"
              />
              <FeatureBox
                title={mobileFeaturesData?.prevent_infections ?? ''}
                subtitle={mobileFeaturesData?.prevent_infections_subtitle ?? ''}
                colour="periwinkle"
              />
              <FeatureBox
                title={mobileFeaturesData?.custom_access_rules ?? ''}
                subtitle={mobileFeaturesData?.custom_access_rules_subtitle ?? ''}
                colour="periwinkle"
              />
              <FeatureBox
                title={mobileFeaturesData?.browse_faster ?? ''}
                subtitle={mobileFeaturesData?.browse_faster_subtitle ?? ''}
                colour="periwinkle"
              />
            </Grid>
          </Flex>
          <Tag title={mobileFeaturesData?.other_features ?? ''} />
          <Grid columns={[1, 2, 2]} sx={otherFeaturesBaseStyle}>
            <FeatureText icon={noLoggingIcon} text={mobileFeaturesData?.strict_no_log ?? ''} />
            <FeatureText
              icon={dataEncryptedIcon}
              text={mobileFeaturesData?.encrypts_your_data ?? ''}
            />
            <FeatureText
              icon={commandLineIcon}
              text={mobileFeaturesData?.import_and_connect ?? ''}
            />
            <FeatureText icon={globeIcon} text={mobileFeaturesData?.servers_in_over ?? ''} />
          </Grid>

          <FeaturePageDownloadButtons path={path} hideSignUp />
          <FeatureSocialProof
            data={
              data.directus?.mobile_page
                ?.social_proof as Queries.DirectusData_component_social_proof
            }
          />
          <Flex
            sx={{
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: theme => `calc(${theme.sizes?.['maxContentWidth']} + 0.8rem)`,
              width: '100%',
            }}
          >
            <ComponentFAQ
              data={
                {
                  '/features/android/': data.directus?.mobile_page?.android_faq,
                  '/features/ios/': data.directus?.mobile_page?.ios_faq,
                }[path] as Queries.DirectusData_component_faq
              }
            />
          </Flex>
          <Text variant="paragraphSmallest" sx={{ maxWidth: '57.2rem', textAlign: 'center' }}>
            {mobileFeaturesData?.tldr ?? ''}
          </Text>
        </ContentBlock>
      </Flex>
    </StaticPageLayout>
  )
}
